import { computed } from 'vue'
import { required, email, helpers } from '@vuelidate/validators'
import { useLocaleStore } from '@/store/localStore'

const phoneRegex = /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/
const urlRegex = /^(https?:\/\/)?([\da-z.-]+)\.([a-z.]{2,6})([/\w .-]*)*\/?$/

const validationMessages = {
  en: {
    required: 'This field cannot be empty.',
    email: 'Please enter a valid email address.',
    number: 'Please enter only numbers.',
    phone: 'Please enter a valid phone number.',
    url: 'Please enter a valid URL.',
    invalid: 'Invalid input.'
  },
  de: {
    required: 'Dieses Feld darf nicht leer sein.',
    email: 'Bitte geben Sie eine gültige E-Mail-Adresse ein.',
    number: 'Bitte geben Sie nur Zahlen ein.',
    phone: 'Bitte geben Sie eine gültige Telefonnummer ein.',
    url: 'Bitte geben Sie eine gültige URL ein.',
    invalid: 'Ungültige Eingabe.'
  }
}

function getLocalizedMessage(key: keyof typeof validationMessages.en): string {
  const localeStore = useLocaleStore()
  const locale = localeStore.getLocale as keyof typeof validationMessages
  return validationMessages[locale]?.[key] || validationMessages.en[key]
}

export function useValidation(field: { type: string; optional: boolean }) {
  const rules = computed(() => ({
    value: {
      required: !field.optional
        ? helpers.withMessage(getLocalizedMessage('required'), required)
        : false,
      validationRules: helpers.withMessage(
        getErrorMessage(field.type),
        (value: any, siblingState: any, vm: any) => {
          // If the field is optional and empty, it's valid
          if (field.optional && (value === '' || value === null || value === undefined)) {
            return true
          }

          // Apply specific validation based on field type
          switch (field.type) {
            case 'emailinput':
              return email.$validator(value, siblingState, vm)
            case 'numberinput':
              return !isNaN(Number(value))
            case 'phoneinput':
              return phoneRegex.test(value)
            case 'urlinput':
              return urlRegex.test(value)
            default:
              return true
          }
        }
      )
    }
  }))

  return {
    rules
  }
}

function getErrorMessage(fieldType: string): string {
  switch (fieldType) {
    case 'emailinput':
      return getLocalizedMessage('email')
    case 'numberinput':
      return getLocalizedMessage('number')
    case 'phoneinput':
      return getLocalizedMessage('phone')
    case 'urlinput':
      return getLocalizedMessage('url')
    default:
      return getLocalizedMessage('invalid')
  }
}
